/**
 * Favourites - a device-local list of saved products.
 *
 * Why snapshots: the obvious build stores handles and fetches each product when
 * the list is opened, which costs one request per favourite and leaves the page
 * blank until they land. Here each entry carries enough to draw its own card
 * (title, image, price, availability), so the list paints immediately from
 * localStorage with no network at all. Prices do drift, so entries older than
 * STALE_MS are refreshed in the background, a few at a time, and the cards are
 * corrected in place - the visitor sees content first and accuracy a moment later.
 *
 * State lives only in this browser. Nothing is sent anywhere.
 */
(() => {
  'use strict';

  const KEY = 'cc:favs';
  const MAX = 100; // keeps localStorage small and the page quick
  const STALE_MS = 6 * 60 * 60 * 1000; // re-check a snapshot at most every 6 hours
  const CONCURRENCY = 6; // parallel refreshes; the rest queue

  /* ---------- store ---------- */

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((x) => x && typeof x.h === 'string') : [];
    } catch (e) {
      return []; // private mode, blocked storage, or corrupt JSON - favourites just stay off
    }
  };

  const write = (list) => {
    try {
      if (list.length) localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      else localStorage.removeItem(KEY); // no empty array left lying around
    } catch (e) { /* nothing to do - the UI already reflects the change */ }
    document.dispatchEvent(new CustomEvent('cc:favs-changed', { detail: { count: list.length } }));
  };

  const has = (handle) => read().some((x) => x.h === handle);

  const add = (entry) => {
    const list = read().filter((x) => x.h !== entry.h);
    list.unshift(Object.assign({ ts: Date.now() }, entry));
    write(list);
  };

  const remove = (handle) => write(read().filter((x) => x.h !== handle));

  /* ---------- formatting ---------- */

  // Prices are held in minor units, the same shape /products/<handle>.js returns.
  const money = (pence) => {
    if (typeof pence !== 'number' || !isFinite(pence)) return '';
    return '£' + (pence / 100).toFixed(2);
  };

  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // Ask the CDN for a card-sized image rather than the full-resolution original.
  const sized = (url, w) => {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + w);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  };

  const parsePrice = (text) => {
    const m = String(text || '').replace(/,/g, '').match(/(\d+)(?:\.(\d{1,2}))?/);
    if (!m) return null;
    const pence = (m[2] || '0').length === 1 ? m[2] + '0' : (m[2] || '00');
    return parseInt(m[1], 10) * 100 + parseInt(pence, 10);
  };

  /* ---------- reading a product out of whatever card it is in ---------- */

  const handleFromHref = (href) => {
    const m = (href || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  };

  const readCard = (card) => {
    if (!card) return {};

    // Horizon's own card carries a JSON payload, so no scraping is needed.
    if (card.tagName === 'PRODUCT-CARD') {
      let payload = null;
      try { payload = JSON.parse(card.getAttribute('view-event-payload') || 'null'); } catch (e) {}
      const product = payload && payload.product;
      const link = card.querySelector('a[href*="/products/"]');
      const img = card.querySelector('img');
      const priceEl = card.querySelector('.price, [class*="price"]');
      return {
        h: (product && product.handle) || handleFromHref(link && link.getAttribute('href')),
        t: (product && product.title) || (link ? link.textContent.trim() : ''),
        img: img ? (img.currentSrc || img.src) : '',
        // Left null when it cannot be read - the refresh pass fills it in.
        p: priceEl ? parsePrice(priceEl.textContent) : null,
        a: true
      };
    }

    // Our own card, and the cards on the favourites page itself.
    const link = card.querySelector('.cc-card-link');
    const img = card.querySelector('.cc-shot img');
    const priceEl = card.querySelector('.cc-price');
    const title = card.querySelector('.cc-title');
    // A card with a quick-add form is a single-variant product: keep its variant
    // so the favourites page can offer the same one-tap Add.
    const idInput = card.querySelector('form.cc-add input[name="id"]');
    return {
      h: handleFromHref(link && link.getAttribute('href')),
      t: title ? title.textContent.trim() : '',
      img: img ? (img.currentSrc || img.src) : '',
      p: priceEl ? parsePrice(priceEl.textContent) : null,
      a: !card.querySelector('.cc-stock--out, .cc-badge--out'),
      v: idInput ? Number(idInput.value) : undefined,
      single: idInput ? true : undefined
    };
  };

  /* ---------- the button ---------- */

  const ICON = '<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M12 20.5C12 20.5 3.5 15.5 3.5 9.5a4.5 4.5 0 0 1 8.5-2.1 4.5 4.5 0 0 1 8.5 2.1c0 6-8.5 11-8.5 11z"/></svg>';

  const paint = (btn, saved) => {
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', saved ? 'Remove from favourites' : 'Save to favourites');
    btn.classList.toggle('is-on', !!saved);
    // The product page button is labelled; the card hearts are not.
    const label = btn.querySelector('[data-cc-fav-label]');
    if (label) label.textContent = saved ? (btn.dataset.labelOn || 'Saved') : (btn.dataset.labelOff || 'Save to favourites');
  };

  const button = (handle, saved) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cc-fav';
    b.dataset.ccFav = handle;
    b.innerHTML = ICON;
    paint(b, saved);
    return b;
  };

  /* ---------- putting buttons on cards ---------- */

  const CARDS = 'product-card, .cc-card';

  const enhance = (root) => {
    const saved = {};
    read().forEach((x) => { saved[x.h] = true; });
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(CARDS).forEach((card) => {
      if (card.querySelector(':scope > .cc-fav')) return;
      const info = readCard(card);
      if (!info.h) return;
      card.classList.add('cc-has-fav');
      card.appendChild(button(info.h, !!saved[info.h]));
    });
  };

  // One delegated listener for every button on the page, however it got there.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest ? e.target.closest('.cc-fav') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.dataset.ccFav;
    if (!handle) return;
    const card = btn.closest('product-card, .cc-card, .cc-fcard');
    const nowSaved = !has(handle);

    if (nowSaved) {
      let info = null;
      if (btn.dataset.ccFavEntry) {
        // Rendered by Liquid with the real product - no scraping needed.
        try { info = JSON.parse(btn.dataset.ccFavEntry); } catch (e) { info = null; }
      }
      if (!info) info = readCard(card);
      info.h = handle;
      add(info);
    } else {
      remove(handle);
    }

    // A product can appear in several rails at once; keep every copy in step.
    const sel = '.cc-fav[data-cc-fav="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]';
    document.querySelectorAll(sel).forEach((b) => paint(b, nowSaved));

    // On the list itself, un-saving should take the card away.
    if (!nowSaved && card && card.classList.contains('cc-fcard')) card.remove();

    toast(nowSaved ? 'Saved to favourites' : 'Removed from favourites');
  });

  /* ---------- toast ---------- */

  let toastTimer = null;
  const toast = (message) => {
    let el = document.querySelector('.cc-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cc-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.innerHTML = esc(message) + ' <a href="/pages/favourites">View favourites</a>';
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-on'), 3200);
  };

  /* ---------- the favourites page ---------- */

  const page = () => document.querySelector('[data-cc-favs-page]');

  // Same markup and look as every other product card (snippets/cc-product-card
  // and cc-add-button): photo on a tint, price, and the bag button - which adds a
  // single-variant product straight to the cart and opens the page otherwise.
  const BAG = '<span class="cc-add-icon" aria-hidden="true"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"/></svg></span>';

  const cardHTML = (x) => {
    const url = '/products/' + encodeURIComponent(x.h);
    const buyable = !x.gone && x.a !== false;
    let badge = '';
    if (x.gone) badge = '<span class="cc-badge cc-badge--out">No longer available</span>';
    else if (x.a === false) badge = '<span class="cc-badge cc-badge--out">Sold out</span>';
    else if (typeof x.stock === 'number' && x.stock > 0 && x.stock <= 10) badge = '<span class="cc-badge cc-badge--low">Only ' + x.stock + ' left</span>';

    let add = '';
    if (buyable && x.single && x.v) {
      add = '<form class="cc-add" action="/cart/add" method="post">' +
        '<input type="hidden" name="id" value="' + esc(x.v) + '"><input type="hidden" name="quantity" value="1">' +
        '<button class="cc-add-btn" type="submit" aria-label="Add ' + esc(x.t) + ' to cart">' + BAG +
        '<span class="cc-add-text"><span class="cc-add-label">Add</span><span class="cc-add-done" aria-hidden="true">Added</span></span></button></form>';
    } else if (buyable) {
      add = '<a class="cc-add-btn cc-add-btn--choose" href="' + esc(url) + '" aria-label="View ' + esc(x.t) + '">' + BAG +
        '<span class="cc-add-text"><span class="cc-add-label">View</span></span></a>';
    }

    return '<li class="cc-card cc-fcard' + (x.gone ? ' is-gone' : '') + '" data-fav-item="' + esc(x.h) + '">' +
      '<div class="cc-shot">' + (x.img ? '<img src="' + esc(sized(x.img, 480)) + '" alt="" width="480" height="480" loading="lazy">' : '') + badge + '</div>' +
      '<p class="cc-title"><a class="cc-card-link" href="' + esc(url) + '">' + esc(x.t) + '</a></p>' +
      '<div class="cc-buy"><span class="cc-price">' + money(x.p) + '</span>' + add + '</div>' +
      '<button type="button" class="cc-fav is-on" data-cc-fav="' + esc(x.h) + '" aria-pressed="true" aria-label="Remove from favourites">' + ICON + '</button>' +
      '</li>';
  };

  const setCount = (n) => {
    document.querySelectorAll('[data-cc-favs-count]').forEach((el) => {
      el.textContent = n ? n + (n === 1 ? ' item' : ' items') : '';
    });
  };

  const renderOne = (x) => {
    const host = page();
    if (!host) return;
    const sel = '[data-fav-item="' + (window.CSS && CSS.escape ? CSS.escape(x.h) : x.h) + '"]';
    const li = host.querySelector(sel);
    if (li) li.outerHTML = cardHTML(x);
  };

  const renderPage = () => {
    const host = page();
    if (!host) return;
    const list = read();
    setCount(list.length);
    badge();

    if (!list.length) {
      host.innerHTML = '<div class="cc-empty cc-favs-empty">' +
        '<p>Nothing saved yet. Tap the heart on anything you like the look of and it will be waiting here.</p>' +
        '<a class="cc-btn2 cc-btn2--terra" href="/">Start browsing</a></div>';
      return;
    }

    // Straight from localStorage - no network, so this is on screen immediately.
    host.innerHTML = '<ul class="cc-fgrid">' + list.map(cardHTML).join('') + '</ul>';

    const stale = list.filter((x) => !x.ts || !x.v || Date.now() - x.ts > STALE_MS).map((x) => x.h);
    if (stale.length) refresh(stale);
  };

  /* ---------- background refresh ---------- */

  const refresh = async (handles) => {
    const queue = handles.slice();
    const list = read();
    let changed = false;

    const worker = async () => {
      while (queue.length) {
        const handle = queue.shift();
        let data = null;
        try {
          const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
          // A 404 means it is gone or unpublished. Mark it, never silently drop
          // it - the visitor chose to save this and deserves to be told.
          if (res.status === 404) data = 'gone';
          else if (res.ok) data = await res.json();
        } catch (e) { /* offline - keep the snapshot and try again next visit */ }
        if (!data) continue;

        const entry = list.filter((x) => x.h === handle)[0];
        if (!entry) continue;
        changed = true;

        if (data === 'gone') {
          entry.gone = true;
        } else {
          const variants = data.variants || [];
          const variant = variants.find((v) => v.available) || variants[0] || {};
          entry.gone = false;
          entry.v = variant.id;
          entry.single = variants.length === 1;
          entry.t = data.title || entry.t;
          entry.p = typeof data.price === 'number' ? data.price : entry.p;
          entry.a = !!data.available;
          entry.stock = variant.inventory_quantity;
          if (data.featured_image) entry.img = data.featured_image;
        }
        entry.ts = Date.now();
        renderOne(entry);
      }
    };

    const workers = [];
    for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) workers.push(worker());
    await Promise.all(workers);

    if (changed) {
      // Written once at the end, not per product, to avoid thrashing storage.
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
    }
  };

  document.addEventListener('cc:favs-changed', () => {
    const list = read();
    setCount(list.length);
    badge(); // header count follows every change, not just page renders
    if (page() && !list.length) renderPage(); // last one removed - show the empty state
  });

  /* ---------- header entry point ---------- */

  // Fallback only. snippets/cc-header-favourites.liquid normally creates this
  // during parsing, so the icon is there for the first paint; this catches the
  // case where that snippet did not run. Both bail if the link already exists.
  const headerLink = () => {
    const actions = document.querySelector('header-actions');
    if (!actions || actions.querySelector('.cc-favlink')) return;
    const a = document.createElement('a');
    a.className = 'cc-favlink header-actions__action';
    a.href = '/pages/favourites';
    a.setAttribute('aria-label', 'Favourites');
    a.innerHTML = ICON + '<span class="cc-favlink-n" data-cc-favs-badge hidden></span>';
    // Before the account icon, so cart stays last where people expect it.
    actions.insertBefore(a, actions.firstElementChild);
    badge();
  };

  const badge = () => {
    const n = read().length;
    document.querySelectorAll('[data-cc-favs-badge]').forEach((el) => {
      el.textContent = n > 9 ? '9+' : String(n);
      el.hidden = n === 0;
    });
  };

  /* ---------- start ---------- */

  // Buttons can be server-rendered (product page) as well as injected, so state
  // is applied to all of them from one place rather than only at creation.
  const syncAll = () => {
    const saved = {};
    read().forEach((x) => { saved[x.h] = true; });
    document.querySelectorAll('.cc-fav[data-cc-fav]').forEach((b) => paint(b, !!saved[b.dataset.ccFav]));
  };

  const start = () => {
    enhance(document);
    headerLink();
    syncAll();
    renderPage();

    // Rails re-render themselves for personalisation and Horizon swaps card
    // grids on filtering, so new cards have to be picked up as they appear.
    const observer = new MutationObserver((records) => {
      for (let i = 0; i < records.length; i++) {
        const added = records[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (node.nodeType !== 1) continue;
          // Every added node, not just the first: a grid can fill several
          // slots in one go, each its own mutation record.
          if (node.matches && node.matches(CARDS)) { enhance(node.parentNode); continue; }
          if (node.querySelector && node.querySelector(CARDS)) enhance(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.ccFavs = { read: read, add: add, remove: remove, has: has, count: () => read().length };
})();
